import Card from "@shared/components/ui-kit/data-display/Card/Card.web";
import CurrencyFormat from "@shared/components/ui-kit/data-display/CurrencyFormat/CurrencyFormat.web";
import Button from "@shared/components/ui-kit/form/Button/Button.web";
import Icon from "@shared/components/ui-kit/icons/Icon.web";
import { ASSIGNED_SEATING, enumsTypes } from "@shared/constants";
import useAssignedSeatingHelpers from "@shared/hooks/seating/useAssignedSeatingHelpers/useAssignedSeatingHelpers";
import AssignedSeatInformation from "@shared/modules/seating/components/AssignedSeatInformation/AssignedSeatInformation.web";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import { SeatWithLocationDataType } from "@shared/modules/seating/types/seating-types";
import { useTranslation } from "@shared/utils/internationalization";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { BestAvailableSeatingCartBodyProps } from "./BestAvailableSeatingCartBody.props";

export default function BestAvailableSeatingCartBody({
  currency,
  onRemoveItem,
}: Omit<BestAvailableSeatingCartBodyProps, "seatingObjectArray">) {
  const { t } = useTranslation("seating");

  const { handleRemoveSeat } = useAssignedSeatingHelpers();
  const { stageServiceState, synchronizeSeating, seatingObjectArray } =
    useSeatingState();
  const handleRemoveItem = (removedItemId: number) => {
    onRemoveItem(removedItemId);
  };

  const handleRemoveFromCart = async (item: SeatWithLocationDataType) => {
    if (item.name === ASSIGNED_SEATING.GENERAL_ADMISSION.ID) {
      stageServiceState.removeGaSelection(item.id);
      // Ensure state is synchronized after GA removal
      synchronizeSeating();
      handleRemoveItem(item.id);
    } else {
      // Create a proper seatToRemove object for best available seats
      const seatToRemove = {
        $data: item,
        permissionId: item.permissionId || 0,
        itemTypeId: item.ticketTypeId || 0,
        resaleId: undefined,
        itemGroupParent: undefined,
        minPurchaseLimit: undefined,
        payWhatYouCanPrice: undefined,
      };

      // Use handleRemoveSeat to ensure all modal logic (isolated seats, etc.) is triggered
      await handleRemoveSeat({
        $data: item,
        seatToRemove,
      });
      handleRemoveItem(item.id);
    }
  };

  return (
    <Flex
      backgroundColor="bg"
      padding="md"
      justify="center"
      direction="column"
      width="100%"
      gap="md"
    >
      <Flex direction="column" gap="2xs">
        <Flex align="center" gap="xs">
          <Icon.Ticket colorPalette="primary" boxSize="element.lg" />
          <Text fontSize="sm" color="fg" fontWeight="bold">
            {t("Tickets are not reserved yet")}
          </Text>
        </Flex>
        <Text fontSize="sm" color="fg">
          {t("Click continue to secure your tickets")}
        </Text>
      </Flex>
      {seatingObjectArray.map((item) => (
        <Card
          key={item.id}
          width="100%"
          borderColor="gray.400"
          border="outline"
          borderWidth="1px"
          borderRadius="8"
          p="md"
        >
          <Flex position="relative" width="100%">
            <Flex direction="column" flex="1" pr="3xl">
              <Flex justify="space-between" align="center">
                <Text fontSize="md" fontWeight="bold">
                  {`${t("Section")}: ${item.segment_identifier}`}
                </Text>
              </Flex>

              <Flex justify="space-between" align="center">
                <Text fontSize="sm">{item.label}</Text>
              </Flex>

              {/* Price and rank badge row */}
              <Flex justify="space-between" align="center">
                <Text fontSize="sm">
                  <CurrencyFormat
                    currency={currency as enumsTypes.CurrencyMapIdType}
                    value={item.price}
                    displayType="text"
                    data-testid="best-available-subtotal"
                  />
                </Text>
              </Flex>

              {item.qty && (
                <Flex justify="space-between" align="center" mt="xs">
                  <Text fontSize="sm">
                    {t("Qty")}: {item.qty}
                  </Text>
                </Flex>
              )}

              {/* Seating information */}
              <Box
                bg="bg.subtle"
                p="2xs"
                borderRadius="sm"
                width="100%"
                mr="6xl"
              >
                <AssignedSeatInformation
                  identifier={item.identifier}
                  segment_identifier={item.segment_identifier}
                  location_identifier={item.location_identifier}
                  isAccessible={item.is_accessible}
                />
              </Box>
            </Flex>

            <Button
              position="absolute"
              right="none"
              top="none"
              height="100%"
              width="element.2xl"
              size="xs"
              variant="faded"
              alignItems="center"
              justifyContent="center"
              colorPalette="red"
              data-testid="basket-item-remove"
              onClick={() => {
                handleRemoveFromCart(item);
              }}
            >
              <Icon.X.Outline w="element.xl" h="element.xl" />
            </Button>
          </Flex>
        </Card>
      ))}
    </Flex>
  );
}
